'use client'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { IProfile } from '@/types'
import { IChangePasswordRequest, IUpdateProfileRequest } from '@/types/auth'

import { toApiError } from '@/utils/errors/toApiError'

import settings from '@/settings/public'

const updateProfile = async (params: IUpdateProfileRequest): Promise<IProfile> => {
  const url = `${settings.api.host}/api/1.0/auth/users/profile`
  const requestBody = {
    data: params
  }

  const response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  })

  const responseJson = await response.json()

  // toApiError keeps `code` and `userMessage` on the thrown Error, which is what
  // lets a caller tell a field-level rejection (42218) from a real failure.
  if (responseJson.error) {
    throw toApiError(responseJson.error)
  }

  return responseJson
}

const changePassword = async (params: IChangePasswordRequest): Promise<IProfile> => {
  const url = params.hash
    ? `${settings.api.host}/api/1.0/auth/users/password/recover`
    : `${settings.api.host}/api/1.0/auth/users/password/change`
  const requestBody = {
    data: params
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  })

  const responseJson = await response.json()

  if (responseJson.error) {
    const err = responseJson.error?.extra?.info || responseJson.error?.userMessage?.original || responseJson.error
    throw new Error(err)
  }

  return responseJson
}

const deleteAccount = async (): Promise<IProfile> => {
  const url = `${settings.api.host}/api/1.0/auth/users/profile/cancellation`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  })

  const responseJson = await response.json()

  if (responseJson.error) {
    const err = responseJson.error?.extra?.info || responseJson.error?.userMessage?.original || responseJson.error
    throw new Error(err)
  }

  return responseJson
}

const reactivateAccount = async (): Promise<IProfile> => {
  const url = `${settings.api.host}/api/1.0/auth/users/profile/reactivation`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  })

  const responseJson = await response.json()

  if (responseJson.error) {
    const err = responseJson.error?.extra?.info || responseJson.error?.userMessage?.original || responseJson.error
    throw new Error(err)
  }

  return responseJson
}

export const useProfile = () => {
  const queryClient = useQueryClient()

  const updateProfileMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['profile'],
      })
    }
  })

  const changePasswordMutation = useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['profile'],
      })
    }
  })

  const deleteAccountMutation = useMutation({
    mutationFn: deleteAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['profile'],
      })
    }
  })

  const reactivateAccountMutation = useMutation({
    mutationFn: reactivateAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['profile'],
      })
    }
  })

  return {
    updateProfile: updateProfileMutation.mutate,
    isPendingUpdateProfile: updateProfileMutation.isPending,
    isSuccessUpdateProfile: updateProfileMutation.isSuccess,
    isErrorUpdateProfile: updateProfileMutation.isError,
    updateProfileMutation,
    changePassword: changePasswordMutation.mutate,
    isPendingChangePassword: changePasswordMutation.isPending,
    isSuccessChangePassword: changePasswordMutation.isSuccess,
    isErrorChangePassword: changePasswordMutation.isError,
    changePasswordMutation,
    deleteAccount: deleteAccountMutation.mutate,
    isPendingDeleteAccount: deleteAccountMutation.isPending,
    deleteAccountMutation,
    reactivateAccount: reactivateAccountMutation.mutate,
    isPendingReactivateAccount: reactivateAccountMutation.isPending,
    reactivateAccountMutation,
  }
}